import api from './api.service';
import type { ApiResponse } from '@/types';

function unwrap<T>(res: unknown): T | undefined {
  if (!res || typeof res !== 'object') return undefined;
  const r = res as ApiResponse<T>;
  if (r.data !== undefined) return r.data as T;
  return res as T;
}

export type CrmCalendarKind = 'activity' | 'follow_up' | 'demo' | 'task' | 'reminder';
export type CrmCalendarLayer = 'logs' | 'followups' | 'demos' | 'personal';
export type CrmCalendarScope = 'mine' | 'team';

export interface CrmCalendarEvent {
  kind: CrmCalendarKind;
  id: string;
  title: string;
  start: string;
  end: string | null;
  allDay: boolean;
  status: string | null;
  ownerId: string | null;
  ownerName: string | null;
  party: string | null;
  link: string;
  notes: string | null;
  remindMinutesBefore: number | null;
  leadId: string | null;
  opportunityId: string | null;
  customerId: string | null;
}

export interface CrmCalendarEntryInput {
  entry_kind: 'task' | 'reminder';
  title: string;
  notes?: string | null;
  all_day?: boolean;
  starts_at: string;
  ends_at?: string | null;
  remind_minutes_before?: number | null;
  lead_id?: string | null;
  opportunity_id?: string | null;
  customer_id?: string | null;
}

const crmCalendarService = {
  async list(params: {
    from: string;
    to: string;
    scope: CrmCalendarScope;
    layers: CrmCalendarLayer[];
  }): Promise<CrmCalendarEvent[]> {
    const res = await api.get('/crm/calendar', {
      params: { ...params, layers: params.layers.join(',') },
    });
    return unwrap<CrmCalendarEvent[]>(res) ?? [];
  },

  async get(id: string): Promise<CrmCalendarEvent> {
    const res = await api.get(`/crm/calendar/entries/${id}`);
    const data = unwrap<CrmCalendarEvent>(res);
    if (!data) throw new Error('Calendar item not found');
    return data;
  },

  async create(body: CrmCalendarEntryInput): Promise<CrmCalendarEvent> {
    const res = await api.post('/crm/calendar/entries', body);
    const data = unwrap<CrmCalendarEvent>(res);
    if (!data) throw new Error('Could not save the calendar item');
    return data;
  },

  async update(id: string, body: Partial<CrmCalendarEntryInput>): Promise<CrmCalendarEvent> {
    const res = await api.patch(`/crm/calendar/entries/${id}`, body);
    const data = unwrap<CrmCalendarEvent>(res);
    if (!data) throw new Error('Could not update the calendar item');
    return data;
  },

  async complete(id: string): Promise<CrmCalendarEvent> {
    const res = await api.post(`/crm/calendar/entries/${id}/complete`, {});
    const data = unwrap<CrmCalendarEvent>(res);
    if (!data) throw new Error('Could not complete the calendar item');
    return data;
  },

  async cancel(id: string): Promise<CrmCalendarEvent> {
    const res = await api.post(`/crm/calendar/entries/${id}/cancel`, {});
    const data = unwrap<CrmCalendarEvent>(res);
    if (!data) throw new Error('Could not cancel the calendar item');
    return data;
  },
};

export default crmCalendarService;
